import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { map, Observable, takeWhile } from 'rxjs';
import { TableRowDefUIDirective } from '../../directives/table-row-def.directive';
import { TableResizeUIService } from '../../services/table-resize.service';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { TableGetCellPipe } from '../../pipes/table-get-cell.pipe';
import { OpenItemDirective } from '@shared-util-dom';

@Component({
  selector: 'fr8bot-ui-table-row',
  // Every row opens its item the same way: double-click, or a touch/pen tap.
  hostDirectives: [{ directive: OpenItemDirective, outputs: ['fr8botOpenItem'] }],
  host: {
    '[style.grid-template-columns]': 'gridTemplateColumns()',
    '[class.tw:cursor-pointer]': 'openable()',
  },
  templateUrl: './table-row.component.html',
  styleUrl: './table-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, AsyncPipe, TableGetCellPipe],
})
export class TableRowUIComponent<DataType> {
  protected rowDefDirective = inject<TableRowDefUIDirective<DataType>>(TableRowDefUIDirective, {
    host: true,
  });
  private resizeService = inject(TableResizeUIService);

  /** False for a table whose rows open nothing, so the row doesn't look clickable. */
  readonly openable = input(true);

  protected readonly gridTemplateColumns = this.resizeService.gridTemplateColumns;
  public readonly rowIndex = this.rowDefDirective.getRowIndex();
  public readonly rowData$: Observable<DataType> = this.rowDefDirective.rowsData$.pipe(
    takeWhile((rowsData: DataType[]) => rowsData.length > this.rowIndex),
    map((rowsData: DataType[]) => {
      const row = rowsData[this.rowIndex]!;
      this.rowDefDirective.incrementRowIndex();

      return row;
    }),
  );

  trackByFn(index: number, item: string): string {
    return `cell_${index}_${item}`;
  }
}
